import React, { useState } from 'react';
import {
  TextBody,
  Box,
  Space,
  Dejargonator,
  RadioButton,
  RadioGroup,
  Button,
  Icon,
} from '@ally/metronome-ui';
import { AccordionCard, UL, LI } from '../../atoms';
import { PendingAccount } from '../../../models';

import styled from 'styled-components';
import { muiEyeballHide, muiEyeballShow } from '@ally/metronome-icons';
import TinModal from '../../molecules/TinModal';
import TinCertContent from './textContents';
import DOMPurify from 'dompurify';

const NumberLI = styled(LI)`
  list-style: decimal;
  font-size: 1.067rem;
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StyledOL = styled(UL)`
  padding-inline-start: 40px;
  list-style-type: decimal;
`;

const StyledTextBodyWithBorder = styled(TextBody)`
  ${({ theme: { colors } }): string => `
    border-bottom: 1px dotted ${colors['slate-500']};
    width: fit-content;
  `}
`;

const StyledIconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  legend {
    @media screen and (min-width: 1px) {
      font-size: 15px;
    }
  }
`;

interface IProps {
  account: PendingAccount;
  onBackupWithholdingChange?: (accountId: string, value: string) => void;
  backupWithholdingValue?: boolean;
  shown?: boolean;
  submitCard?: () => void;
  isSubmitted?: boolean;
}

const TinCertificationAccordion: React.FC<IProps> = (props) => {
  const [isTinVisible, setIsTinVisible] = useState<boolean>(false);

  const toggleTin = () => {
    setIsTinVisible((prev) => !prev);
  };

  const maskTin = (tin: string): string => {
    const bulletString = '\u2022';
    return tin.replace(
      /^(\d){3}-(\d){2}-/,
      `${bulletString}${bulletString}${bulletString}-${bulletString}${bulletString}-`
    );
  };

  const displayTin = (val: string): string => {
    return isTinVisible ? val : maskTin(val);
  };

  const getCurrentDate = () => {
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };

  const isCust = props.account.accountType === 'Custodial';

  // Helper function to sanitize text and prevent XSS attacks
  const sanitize = (text: string | undefined): string => {
    if (!text) return '';
    // Strip all HTML tags for sensitive data (TIN, names)
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
  };

  const handlePrintOrDownload = () => {
    const w = window.open('', '', 'width=1000,height=800');
    if (!w) {
      console.error('Popup blocker prevented printing');
      throw new Error('Popup blocker is enabled');
    }
    const tin = ((isCust
      ? props.account.minorInformation?.minorSSN
      : props.account.primaryInformation?.primarySSN) || '') as string;
    const mTin = sanitize(maskTin(tin));
    const aType = isCust ? 'Minor' : 'Primary applicant';
    const aName = sanitize(
      isCust
        ? props.account.minorInformation?.minorName
        : props.account.primaryInformation?.primaryName
    );

    const getFooterContent = (): string => {
      const footerContainer = document.querySelector('footer');
      if (!footerContainer) return '';

      const paragraphs = Array.from(footerContainer.querySelectorAll('p'));
      return paragraphs
        .map((p) => {
          const text = sanitize(p.textContent?.trim());
          return `<p class="print-footer-p">${text}</p>`;
        })
        .join('');
    };

    const cleanFooterParagraphs = getFooterContent();
    const html = ` <!DOCTYPE html>
      <html>
        <head>
            <title>${sanitize(
              isCust
                ? TinCertContent.titleCustodial
                : TinCertContent.titleNonCustodial
            )}</title>
            <style>
            .marginBottom10, .print-footer-p {
              margin-bottom: 10px;
            }
            @page { size: auto; margin: 0; }
            * { box-sizing: border-box; }
            body { size: letter; margin: 0.9in 0.85in; font-size: 16px; 
            line-height: 1.5; font-family: Lato, Arial, Helvetica, sans-serif; 
            color: #000; }
            h1 {
              margin-left: auto;
              margin-right: auto;
              font-size: 22px;
              font-weight: bold;
              text-align: center;
              line-height: 1.3; 
            }
            p { margin: 0 }
            .label {color: gray; font-weight: 700}
            .print-footer-p { font-size: 11px; line-height: 1.5; }
            .print-footer { font-size: 10px; page-break-before: always; margin-top: 0.9in; }
            .underlineText { text-decoration: underline;}
            ol { padding-left:15px}
            </style>
        </head>
        <body>
          <h1>${sanitize(
            isCust
              ? TinCertContent.titleCustodial
              : TinCertContent.titleNonCustodial
          )}</h1>
          <div class="marginBottom10">
            <p class="label">${aType}'s name:</p>
            <p>${aName}</p>
          </div>
          <div class="marginBottom10">
            <p class="label">${aType}'s TIN:</p>
            <p>${mTin}</p>
          </div>
          <div class="marginBottom10">
            <p class="label">Date</p>
            <p>${getCurrentDate()}</p>
          </div>
          <div class="marginBottom10">
            <p>${sanitize(TinCertContent.fedTextP1)} ${
      isCust ? "minor's" : 'your'
    } <span class="underlineText"><b>${sanitize(
      TinCertContent.fedTextTin1
    )} ${sanitize(TinCertContent.fedTextTin2)}*</b></span> ${' '} ${sanitize(
      TinCertContent.fedTextP2
    )} ${' '}<span class="underlineText"><b>${sanitize(
      TinCertContent.fedTextBkpWithHold
    )} ${sanitize(TinCertContent.fedTextP3)}</b></span> <b>**.</b> ${sanitize(
      TinCertContent.fedTextP4
    )}</p>
          </div>
          <div class="marginBottom10">
            <p class="label">${sanitize(
              isCust
                ? TinCertContent.notifiedByIRSMinor
                : TinCertContent.notifiedbyIRS
            )}</p>
            <p>[${
              !props.backupWithholdingValue
                ? ' ✓ '
                : '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'
            }] ${sanitize(
      isCust ? TinCertContent.noNotifiedMinor : TinCertContent.noNotified
    )}</p>
            <p>[${
              props.backupWithholdingValue
                ? ' ✓ '
                : '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'
            }] ${sanitize(
      isCust ? TinCertContent.yesNotifiedMinor : TinCertContent.yesNotified
    )}</p>
          </div>
          <div class="marginBottom10">
            <p>${sanitize(TinCertContent.penaltyText)}</p>
            <ol>
              <li class="marginBottom10">${sanitize(
                isCust ? TinCertContent.listCustodial1 : TinCertContent.list1
              )}</li>
              <li class="marginBottom10">${sanitize(
                isCust ? TinCertContent.listCustodial2 : TinCertContent.list2
              )}</li>
              <li class="marginBottom10">${sanitize(
                isCust ? TinCertContent.listCustodial3 : TinCertContent.list3
              )}</li>
              <li class="marginBottom10">${sanitize(
                isCust ? TinCertContent.listCustodial4 : TinCertContent.list4
              )}</li>
            </ol>
          </div>
          <div class="marginBottom10">
            <p>${sanitize(TinCertContent.fatcaText)}</p>
          </div>
          <div class="marginBottom10">
            <p>*${`${TinCertContent.tooltipTINP1} ${
              isCust ? "minor's" : 'your'
            } ${TinCertContent.tooltipTINP2} ${isCust ? 'custodial' : ''} ${
              TinCertContent.tooltipTINP3
            }`}</p>
          </div>
          <div class="marginBottom10">
            <p>**${sanitize(TinCertContent.tooltipWithholding)}</p>
          </div>
          <div class="marginBottom10">
            ${cleanFooterParagraphs}
          </div>
        </body>
      </html>`;
    w.document.open();
    w.document.write(html);

    // Guard flag to prevent double-printing
    let printCalled = false;

    const executePrint = () => {
      if (!printCalled) {
        printCalled = true;
        w.focus();
        w.print();
      }
    };

    //Wait for window to load
    w.onload = () => {
      w.document.close();
      executePrint();
    };

    //If onload never fires, print after timeout
    setTimeout(() => {
      w.document.close();
      executePrint();
    }, 1500); // 2 second fallback
  };

  return (
    <AccordionCard
      title={
        isCust
          ? TinCertContent.titleCustodial
          : TinCertContent.titleNonCustodial
      }
      submitButtonContent='Certify and Continue'
      shown={props.shown}
      submitCard={props.submitCard}
      isSubmitted={props.isSubmitted}
    >
      <Space paddingBottom='20px'>
        <TextBody tag='div' size='md' id='tinCertP1'>
          {TinCertContent.fedTextP1} {isCust ? "minor's" : 'your'}{' '}
          <Dejargonator
            ariaLabelPopover='Taxpayer Identification Number'
            heading={TinCertContent.fedTextTin1}
            body={`${TinCertContent.tooltipTINP1} ${
              isCust ? "minor's" : 'your'
            } ${TinCertContent.tooltipTINP2} ${isCust ? 'custodial' : ''} ${
              TinCertContent.tooltipTINP3
            }`}
            trigger={{
              ariaLabel:
                'Select to learn more about Taxpayer Identification Number',
              variant: 'element',
              disableTriggerHighlight: true,
              content: (
                <StyledTextBodyWithBorder tag='p' size='md'>
                  {TinCertContent.fedTextTin1}
                </StyledTextBodyWithBorder>
              ),
            }}
          />{' '}
          {TinCertContent.fedTextTin2} {TinCertContent.fedTextP2}{' '}
          <Dejargonator
            ariaLabelPopover='backup withholding'
            heading='Backup withholding'
            body={TinCertContent.tooltipWithholding}
            trigger={{
              ariaLabel: 'Select to learn more about backup withholding',
              variant: 'element',
              disableTriggerHighlight: true,
              content: (
                <StyledTextBodyWithBorder tag='p' size='md'>
                  {TinCertContent.fedTextBkpWithHold}
                </StyledTextBodyWithBorder>
              ),
            }}
          />{' '}
          {TinCertContent.fedTextP3}. {TinCertContent.fedTextP4}
        </TextBody>
        <StyledRadioGroup
          orientation='row'
          name={`backupWithholding-${props.account.accountIdentifier}`}
          legendText={
            isCust
              ? TinCertContent.notifiedByIRSMinor
              : TinCertContent.notifiedbyIRS
          }
          onChange={(e: any) =>
            props.onBackupWithholdingChange?.(
              props.account.accountIdentifier,
              e
            )
          }
        >
          <RadioButton
            label={
              isCust
                ? TinCertContent.noNotifiedMinor
                : TinCertContent.noNotified
            }
            value='no'
          />
          <RadioButton
            label={
              isCust
                ? TinCertContent.yesNotifiedMinor
                : TinCertContent.yesNotified
            }
            value='yes'
          />
        </StyledRadioGroup>
        <TextBody tag='p' size='md' id='listItemP1'>
          {TinCertContent.penaltyText}
        </TextBody>
        <StyledOL>
          <NumberLI>
            {isCust ? TinCertContent.listCustodial1 : TinCertContent.list1}
          </NumberLI>
          <NumberLI>
            {isCust ? TinCertContent.listCustodial2 : TinCertContent.list2}
          </NumberLI>
          <NumberLI>
            {isCust ? TinCertContent.listCustodial3 : TinCertContent.list3}
          </NumberLI>
          <NumberLI>
            {isCust ? TinCertContent.listCustodial4 : TinCertContent.list4}
          </NumberLI>
        </StyledOL>
        <TextBody tag='p' size='md' id='fatcaText'>
          {TinCertContent.fatcaText}
        </TextBody>
        <TextBody tag='p' size='md'>
          <Button
            onClick={handlePrintOrDownload}
            text='Print or download'
            variant='inline-link'
          />{' '}
          a copy of the Request for Taxpayer Identification Number and
          Certification to keep for your records.
        </TextBody>

        <Box display='flex' flexDirection='row' width='100%' height='100%'>
          <Box
            display='flex'
            flexDirection='row'
            width='35%'
            justifyContent='right'
            paddingRight='20px'
          >
            <strong>
              {`${isCust ? "Minor's" : "Primary applicant's"} name:`}
            </strong>
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            width='35%'
            paddingLeft='20px'
          >
            {isCust
              ? props.account.minorInformation?.minorName
              : props.account.primaryInformation?.primaryName}
          </Box>
        </Box>
        <Box display='flex' flexDirection='row' width='100%' height='100%'>
          <Box
            display='flex'
            flexDirection='row'
            width='35%'
            justifyContent='right'
            paddingRight='20px'
          >
            <strong>
              {`${isCust ? "Minor's" : "Primary applicant's"} TIN:`}
            </strong>
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            width='35%'
            paddingLeft='20px'
          >
            <span id={`tin-display-${props.account.accountIdentifier}`}>
              {displayTin(
                ((isCust
                  ? props.account.minorInformation?.minorSSN
                  : props.account.primaryInformation?.primarySSN) ||
                  '') as string
              )}
            </span>{' '}
            <Space paddingLeft='5px'>
              <StyledIconButton aria-label='Show/Hide TIN' onClick={toggleTin}>
                <Icon
                  icon={isTinVisible ? muiEyeballHide : muiEyeballShow}
                  size='md'
                />
              </StyledIconButton>
            </Space>
            <Space paddingLeft='5px'>
              <TinModal />
            </Space>
          </Box>
        </Box>
        <Box display='flex' flexDirection='row' width='100%' height='100%'>
          <Box
            display='flex'
            flexDirection='row'
            width='35%'
            justifyContent='right'
            paddingRight='20px'
          >
            <strong>Date:</strong>
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            width='35%'
            paddingLeft='20px'
          >
            {getCurrentDate()}
          </Box>
        </Box>

        <TextBody tag='p' size='md'>
          By choosing <strong>Certify and Continue</strong>, I am providing my
          electronic signature and agree that the information above is correct.
        </TextBody>
      </Space>
    </AccordionCard>
  );
};

export default TinCertificationAccordion;
